import { useConnectorTools } from "@/api/connectors";
import { isApiError } from "@/api/utils";
import {
  BackRow,
  EmptySearch,
  EmptyState,
  LcBadge,
  NeutralBadge,
  PaneHead,
  PaneSection,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils";
import { toolTitleFromName } from "@/utils/toolTitle";
import { Connector, ConnectorTool } from "@cohesive/api-client";
import { IntegrationIcon } from "@cohesive/ui-components";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

const SCOPE_LABELS: Record<string, string> = {
  global: "Catalog",
  organization: "Organization",
  "organization-user": "Yours",
};

/** A connector's page in the Connectors pane: what it is, the user's connection, and its tools. */
export function ConnectorDetail({
  connector,
  status,
  actions,
  reachable,
  onConnect,
  pending,
  connecting,
  onBack,
}: {
  connector: Connector;
  /** The connection's line, as the connector's row reads it. Shown only while connected. */
  status: string;
  /** The verbs the connector's row carries. */
  actions: React.ReactNode;
  /** Whether the user reaches the connector, so its tools can be listed. */
  reachable: boolean;
  /** Unset when the connector can't be connected. */
  onConnect?: () => void;
  /** Whether a connect is running, and whether it is this connector's. */
  pending: boolean;
  connecting: boolean;
  onBack: () => void;
}) {
  const name = connector.display_name;
  const disabled = connector.status === "disabled";
  const connection = connector.connection;

  return (
    <div className="flex min-h-0 flex-col gap-6">
      <BackRow label="All connectors" onBack={onBack} />
      <div className="flex items-center gap-3">
        <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-md border border-n-10 bg-n-05">
          <IntegrationIcon
            imageURL={connector.image_url}
            platform={connector.slug}
            name={name}
            className={cn("size-6", disabled && "opacity-40 grayscale")}
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="truncate text-lg font-medium text-n-90">{name}</h3>
            {SCOPE_LABELS[connector.type] && (
              <NeutralBadge>{SCOPE_LABELS[connector.type]}</NeutralBadge>
            )}
          </div>
          <p className="truncate text-sm text-n-50">
            {disabled
              ? "Disabled by admin"
              : connection
                ? status
                : reachable
                  ? "Ready to use · no sign-in needed"
                  : "Not connected"}
          </p>
        </div>
        {connector.status === "deprecated" && (
          <LcBadge tone="warning">Deprecated</LcBadge>
        )}
        {!disabled && connection?.status === "error" && (
          <LcBadge tone="blocked">Error</LcBadge>
        )}
        {actions}
      </div>
      {connector.description && (
        <p className="text-sm leading-relaxed text-n-60">
          {connector.description}
        </p>
      )}

      {!disabled &&
        (reachable ? (
          <ConnectorTools
            connector={connector}
            onConnect={onConnect}
            pending={pending}
            connecting={connecting}
          />
        ) : (
          <PaneSection head="Tools">
            <NotConnected
              title={
                connection?.status === "error"
                  ? "Reconnect to see this connector's tools"
                  : "Connect to see this connector's tools"
              }
              onConnect={onConnect}
              pending={pending}
              connecting={connecting}
              label={connection ? "Reconnect" : "Connect"}
            />
          </PaneSection>
        ))}
    </div>
  );
}

function ConnectorTools({
  connector,
  onConnect,
  pending,
  connecting,
}: {
  connector: Connector;
  onConnect?: () => void;
  pending: boolean;
  connecting: boolean;
}) {
  const {
    data: tools,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useConnectorTools(connector.id, connector.connection?.id);
  const [searchTerm, setSearchTerm] = useState("");
  const name = connector.display_name;

  const matches = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (tools ?? []).filter(
      (tool) =>
        !term ||
        tool.name.toLowerCase().includes(term) ||
        toolTitle(tool).toLowerCase().includes(term) ||
        tool.description?.toLowerCase().includes(term),
    );
  }, [tools, searchTerm]);

  if (isLoading) {
    return (
      <div aria-busy className="flex flex-col gap-6">
        <div className="px-2.5">
          <Skeleton className="h-8 w-full max-w-72" />
        </div>
        <PaneSection head="Tools">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="flex flex-col gap-1.5 px-2.5 py-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-full max-w-md" />
            </div>
          ))}
        </PaneSection>
      </div>
    );
  }

  if (error && !tools) {
    const code = isApiError(error) ? error.data?.error?.code : undefined;
    if (code === "connector_not_connected") {
      return (
        <PaneSection head="Tools">
          <NotConnected
            title="Connect to see this connector's tools"
            onConnect={onConnect}
            pending={pending}
            connecting={connecting}
            label="Connect"
          />
        </PaneSection>
      );
    }
    const rejected = code === "connector_auth_rejected";
    return (
      <PaneSection head="Tools">
        <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
          <p className="text-base text-n-60">
            {rejected
              ? `${name} rejected your connection.`
              : `Couldn't reach ${name}.`}
          </p>
          <div className="flex gap-2">
            {rejected && onConnect && (
              <Button
                variant="secondary"
                size="sm"
                className={smButtonClassName}
                disabled={pending}
                isLoading={connecting}
                onClick={onConnect}
              >
                Reconnect
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className={smButtonClassName}
              disabled={isFetching}
              isLoading={isFetching}
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </div>
        </div>
      </PaneSection>
    );
  }

  if (tools?.length === 0) {
    return (
      <PaneSection head="Tools">
        <EmptyState title={`${name} lists no tools.`} />
      </PaneSection>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PaneHead
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search tools"
      />
      {matches.length === 0 ? (
        <EmptySearch
          query={searchTerm}
          noun="tools"
          onClear={() => setSearchTerm("")}
        />
      ) : (
        <PaneSection head={`Tools · ${tools?.length}`}>
          {matches.map((tool) => (
            <ToolRow key={tool.name} tool={tool} />
          ))}
        </PaneSection>
      )}
    </div>
  );
}

function toolTitle(tool: ConnectorTool) {
  return tool.title || toolTitleFromName(tool.name);
}

function ToolRow({ tool }: { tool: ConnectorTool }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md px-2.5 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-md font-medium text-n-90">
          {toolTitle(tool)}
        </span>
        <code className="truncate font-mono text-xs text-n-50">
          {tool.name}
        </code>
        <span className="flex-1" />
        {tool.read_only_hint ? (
          <NeutralBadge>Read-only</NeutralBadge>
        ) : (
          tool.destructive_hint && <LcBadge tone="blocked">Destructive</LcBadge>
        )}
      </div>
      {tool.description && <ToolDescription text={tool.description} />}
    </div>
  );
}

/** Clamped to two lines, with a toggle only when that cuts it short. */
function ToolDescription({ text }: { text: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [clamped, setClamped] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element && !expanded) {
      setClamped(element.scrollHeight > element.clientHeight + 1);
    }
  }, [text, expanded]);

  return (
    <div className="flex flex-col items-start gap-0.5">
      <p
        ref={ref}
        className={cn(
          "whitespace-pre-line text-sm text-n-50 [overflow-wrap:anywhere]",
          !expanded && "line-clamp-2",
        )}
      >
        {text}
      </p>
      {(clamped || expanded) && (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm text-n-60"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
}

function NotConnected({
  title,
  label,
  onConnect,
  pending,
  connecting,
}: {
  title: string;
  label: string;
  onConnect?: () => void;
  pending: boolean;
  connecting: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="text-base text-n-60">{title}</p>
      {onConnect && (
        <Button
          variant="secondary"
          size="sm"
          className={smButtonClassName}
          disabled={pending}
          isLoading={connecting}
          onClick={onConnect}
        >
          {label}
        </Button>
      )}
    </div>
  );
}
